/**
 * Stream chart series palette.
 *
 * Canonical color ordering for chart series. Per `STREAM_VISUALIZATION.md §4.4`,
 * series 0 is always blue, series 1 is always green. This makes side-by-side
 * charts comparable across pages.
 *
 * @example
 * ```ts
 * import { colorForSeries, paletteForCategories } from '@/lib/charts/palette'
 *
 * // Get color for series index
 * const color = colorForSeries(0) // => accentPrimary (blue)
 *
 * // Map categories to colors
 * const categoryColors = paletteForCategories(['support', 'engineering', 'marketing'])
 * ```
 */

import * as tokens from '@/lib/tokens'

// ── Series Palette ──────────────────────────────────────────────────────────

/**
 * Canonical series palette ordering.
 *
 * Series 0 always blue, series 1 always green - across all charts in Stream.
 */
export const SERIES_PALETTE = [
  tokens.accentPrimary,  // 0 - primary blue
  tokens.success,        // 1 - green
  tokens.purple,         // 2 - purple
  tokens.warning,        // 3 - amber
  tokens.foam,           // 4 - light blue
  tokens.orange,         // 5 - orange
  tokens.slate,          // 6 - slate
  tokens.error,          // 7 - red (use sparingly)
] as const

export type SeriesPaletteIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Get the color for a series index. Cycles through the palette if index exceeds length.
 */
export function colorForSeries(index: number): string {
  return SERIES_PALETTE[index % SERIES_PALETTE.length]
}

/**
 * Build a deterministic color mapping for a list of category names.
 *
 * @param categories - Array of category names
 * @returns Record mapping category names to palette colors
 *
 * @example
 * ```ts
 * const colors = paletteForCategories(['support', 'engineering', 'marketing'])
 * // => { support: '#3B82F6', engineering: '#22c55e', marketing: '#a855f7' }
 * ```
 */
export function paletteForCategories(
  categories: string[],
): Record<string, string> {
  return categories.reduce(
    (acc, category, index) => {
      acc[category] = colorForSeries(index)
      return acc
    },
    {} as Record<string, string>,
  )
}

// ── Semantic Color Helpers ──────────────────────────────────────────────────

/**
 * Get semantic status color.
 */
export function colorForStatus(
  status: 'success' | 'warning' | 'error' | 'info',
): string {
  return tokens[status]
}

/**
 * Get category color by event category name.
 */
export function colorForCategory(
  category: 'activity' | 'telemetry' | 'lifecycle' | 'operational' | 'communication',
): string {
  const categoryMap: Record<string, string> = {
    activity: tokens.categoryActivity,
    telemetry: tokens.categoryTelemetry,
    lifecycle: tokens.categoryLifecycle,
    operational: tokens.categoryOperational,
    communication: tokens.categoryCommunication,
  }
  return categoryMap[category] || tokens.textMuted
}

/**
 * Get domain color by business domain name.
 */
export function colorForDomain(
  domain: 'support' | 'engineering' | 'marketing' | 'operations' | 'growth' | 'platform',
): string {
  const domainMap: Record<string, string> = {
    support: tokens.domainSupport,
    engineering: tokens.domainEngineering,
    marketing: tokens.domainMarketing,
    operations: tokens.domainOperations,
    growth: tokens.domainGrowth,
    platform: tokens.domainPlatform,
  }
  return domainMap[domain] || tokens.textMuted
}

// ── Sequential/Diverging Scales ─────────────────────────────────────────────

/**
 * Generate a sequential color scale (for heatmaps, choropleths).
 *
 * @param baseColor - Base color (from tokens)
 * @param steps - Number of steps in the scale
 * @returns Array of colors from light to dark
 */
export function sequentialScale(
  baseColor: string,
  steps: number = 5,
): string[] {
  return Array.from({ length: steps }, (_, i) => {
    const opacity = 0.1 + (i / (steps - 1)) * 0.9
    return tokens.rgba(baseColor, opacity)
  })
}

/**
 * Generate a diverging color scale (for positive/negative comparisons).
 *
 * @param steps - Total steps (odd number recommended)
 * @returns Array of colors from error -> neutral -> success
 */
export function divergingScale(steps: number = 7): string[] {
  const half = Math.floor(steps / 2)
  const result: string[] = []

  // Error side (decreasing intensity)
  for (let i = 0; i < half; i++) {
    const opacity = 0.9 - (i / half) * 0.7
    result.push(tokens.rgba(tokens.error, opacity))
  }

  // Middle (neutral)
  result.push(tokens.rgba(tokens.textMuted, 0.2))

  // Success side (increasing intensity)
  for (let i = 0; i < half; i++) {
    const opacity = 0.2 + ((i + 1) / half) * 0.7
    result.push(tokens.rgba(tokens.success, opacity))
  }

  return result
}
